/* SpawnAnything — deterministic lockstep client (browser, or Node with an injected WebSocket class).

   Timeline: the simulation runs in turns of `ticksPerTurn` ticks. Commands queued while the sim
   executes turn N are submitted for turn N + inputDelay. Every client submits exactly one
   (possibly empty) message per turn; that message doubles as the "I am here" heartbeat, so the
   slowest simulation paces the room (self-clocking). The relay merges all submissions per turn
   and broadcasts them; `onTurn` callbacks see every turn exactly once, strictly in order.

   The sim must call `canAdvance(tick)` before executing `tick` and stall while it returns false. */

import {
  C2S, S2C, LIMITS, encode, decode, validateServerMessage, commandSize, cleanCode, errorText,
} from './protocol.js';

const defaultNow = () => (globalThis.performance?.now ? globalThis.performance.now() : Date.now());
const WS_OPEN = 1;

/** Relay URL for the page that is currently open (same origin, path /ws; Vite proxies it in dev). */
export function defaultRelayUrl(path = '/ws') {
  const loc = globalThis.location;
  if (!loc) return `ws://127.0.0.1:8788${path}`;
  return `${loc.protocol === 'https:' ? 'wss' : 'ws'}://${loc.host}${path}`;
}

/* [room-routing] The Cloudflare relay (worker/relay.js) runs one Durable Object per room, so a socket
   must name its room in the URL: join/resume/reconnect use `?room=CODE`, create uses no room. The Node
   relay (server/relay.js) ignores the query string, so this is harmless there. */
export function relayUrlForRoom(url, room) {
  try {
    const u = new URL(url, globalThis.location?.href);
    if (room) u.searchParams.set('room', room); else u.searchParams.delete('room');
    return u.toString();
  } catch { return url; }
}

/* Minimal event emitter: on() returns an unsubscribe function. */
function emitter() {
  const map = new Map();
  return {
    on(ev, cb) { if (!map.has(ev)) map.set(ev, new Set()); map.get(ev).add(cb); return () => map.get(ev)?.delete(cb); },
    off(ev, cb) { map.get(ev)?.delete(cb); },
    emit(ev, payload) {
      const set = map.get(ev); if (!set) return;
      for (const cb of [...set]) { try { cb(payload); } catch (err) { console.error(`lockstep: '${ev}' handler failed`, err); } }
    },
    count(ev) { return map.get(ev)?.size || 0; },
  };
}

export class LockstepError extends Error {
  constructor(code, message) { super(message || errorText(code)); this.code = code; }
}

export function createLockstepClient({
  url,
  name = 'Spieler',
  WebSocketImpl = globalThis.WebSocket,
  inputDelayTurns = 2,
  ticksPerTurn = 3,        // overridden by the server's value at match start
  adaptiveDelay = true,
  minDelay = 1,
  maxDelay = 6,
  pingIntervalMs = 1000,
  reconnectMs = 15000,
  requestTimeoutMs = 10000,
  now = defaultNow,
} = {}) {
  if (!WebSocketImpl) throw new Error('lockstep: no WebSocket implementation available');
  if (!url) url = defaultRelayUrl();
  const ev = emitter();
  const clampDelay = d => Math.max(minDelay, Math.min(maxDelay, d | 0));

  let ws = null, status = 'idle';            // idle | connecting | open | reconnecting | closed
  let connectPromise = null;
  let intentional = false;
  let pingTimer = null, reconnectTimer = null, reconnectDeadline = 0, reconnectAttempt = 0;
  let rid = 1;
  let sockRoom = null;                       // [room-routing] room the current socket was opened for
  const requests = new Map();                // rid -> { resolve, reject, timer }

  // session / lobby
  let code = null, playerId = 0, token = null, lobby = null;
  // match
  let match = null;                          // { seed, setup, turnMs, ticksPerTurn, graceMs, players }
  let tpt = ticksPerTurn;
  let delivered = -1;                        // last turn handed to onTurn callbacks
  const reorder = new Map();                 // turn -> msg (only used if frames arrive out of order)
  let lastReplayAsk = -1e9;
  let localTurn = -1;                        // turn the sim is currently in (from canAdvance)
  let sentThrough = -1;                      // highest turn we have submitted
  let queue = [];                            // commands waiting for the next submission
  const pending = new Map();                 // turn -> { cmds, at } submitted but not yet delivered
  let delay = clampDelay(inputDelayTurns), lowerStreak = 0;
  // network stats
  let srtt = 0, rttvar = 0, rttSamples = 0, roomMaxRtt = 0;
  const lat = { n: 0, sum: 0, max: 0, min: Infinity };
  let stallTicks = 0, lastStallTick = -1, stalledSince = -1, lastResend = -1e9, resends = 0;
  const turnCbs = new Set();

  /* ---------------- socket ---------------- */

  function rawSend(msg) {
    if (!ws || ws.readyState !== WS_OPEN) return false;
    try { ws.send(encode(msg)); return true; } catch { return false; }
  }

  function openSocket() {
    let sock;
    try { sock = new WebSocketImpl(relayUrlForRoom(url, sockRoom)); } catch (err) { return Promise.reject(new LockstepError('connection', err?.message)); }
    ws = sock;
    return new Promise((resolve, reject) => {
      let opened = false;
      sock.onopen = () => {
        if (ws !== sock) return;
        opened = true;
        startPing();
        resolve();
      };
      sock.onmessage = e => { if (ws === sock) onFrame(e.data); };
      sock.onerror = () => { };
      sock.onclose = e => {
        if (ws !== sock) return;
        stopPing();
        ws = null;
        if (!opened) reject(new LockstepError('connection'));
        onSocketClosed(e);
      };
    });
  }

  function connect() {
    if (status === 'open' && ws?.readyState === WS_OPEN) return Promise.resolve();
    if (connectPromise) return connectPromise;
    intentional = false;
    status = 'connecting';
    connectPromise = openSocket().then(() => { status = 'open'; ev.emit('open'); },
      err => { status = 'closed'; throw err; }).finally(() => { connectPromise = null; });
    return connectPromise;
  }

  function onSocketClosed(e) {
    for (const [, r] of requests) { clearTimeout(r.timer); r.reject(new LockstepError('closed')); }
    requests.clear();
    if (status === 'reconnecting') { scheduleReconnect(); return; }
    if (!intentional && code && token) {
      status = 'reconnecting';
      reconnectDeadline = now() + reconnectMs; reconnectAttempt = 0;
      ev.emit('reconnecting', { code: e?.code });
      scheduleReconnect();
      return;
    }
    status = 'closed';
    ev.emit('close', { code: e?.code, reason: intentional ? 'left' : 'lost' });
  }

  function scheduleReconnect() {
    clearTimeout(reconnectTimer);
    const left = reconnectDeadline - now();
    if (left <= 0) { giveUp('timeout'); return; }
    const wait = Math.min(left, Math.min(2000, 150 * 2 ** reconnectAttempt++));
    reconnectTimer = setTimeout(() => {
      if (status !== 'reconnecting') return;
      openSocket().then(() => {
        rawSend({ type: C2S.RESUME, code, token, lastTurn: delivered });
      }, () => { /* onclose schedules the next attempt */ });
    }, wait);
  }

  function giveUp(reason) {
    clearTimeout(reconnectTimer);
    status = 'closed';
    try { ws?.close(); } catch { }
    ws = null;
    const wasCode = code;
    code = null; token = null; playerId = 0;
    ev.emit('error', { code: 'expired', message: errorText('expired') });
    ev.emit('close', { reason, code: wasCode });
  }

  function startPing() {
    stopPing();
    const ping = () => rawSend({ type: C2S.PING, t: now(), rtt: rttSamples ? Math.round(srtt) : undefined });
    ping();
    pingTimer = setInterval(ping, pingIntervalMs);
    pingTimer.unref?.();
  }
  function stopPing() { clearInterval(pingTimer); pingTimer = null; }

  /* [room-routing] Make sure the socket points at `room` (null = new room); reopens silently if not. */
  async function routeTo(room) {
    if (connectPromise) await connectPromise.catch(() => { });
    if (sockRoom === room) return;
    sockRoom = room;
    if (!ws) return;
    const old = ws;
    ws = null; stopPing();                   // ws !== old: its onclose is ignored (no 'close'/'reconnecting')
    if (status === 'open') status = 'idle';
    try { old.close(1000, 'reroute'); } catch { }
  }

  /* ---------------- requests ---------------- */

  function request(msg) {
    return connect().then(() => new Promise((resolve, reject) => {
      const id = rid++;
      const timer = setTimeout(() => { requests.delete(id); reject(new LockstepError('timeout')); }, requestTimeoutMs);
      requests.set(id, { resolve, reject, timer });
      if (!rawSend({ ...msg, rid: id })) { clearTimeout(timer); requests.delete(id); reject(new LockstepError('connection')); }
    }));
  }
  function settle(m, ok) {
    if (m.rid === undefined || !requests.has(m.rid)) return;
    const r = requests.get(m.rid); requests.delete(m.rid); clearTimeout(r.timer);
    if (ok) r.resolve(m); else r.reject(new LockstepError(m.code, m.message));
  }

  /* ---------------- incoming ---------------- */

  function onFrame(data) {
    const m = decode(data);
    if (!m || !validateServerMessage(m)) return;
    switch (m.type) {
      case S2C.WELCOME:
        code = m.code; playerId = m.playerId; token = m.token;
        sockRoom = m.code; // [room-routing] reconnects go to this room
        settle(m, true);
        ev.emit('welcome', { code, playerId });
        break;
      case S2C.RESUMED: onResumed(m); break;
      case S2C.LOBBY:
        lobby = { code: m.code, state: m.state, hostId: m.hostId, maxPlayers: m.maxPlayers, players: m.players };
        ev.emit('lobby', { ...lobby, you: playerId, isHost: m.hostId === playerId });
        break;
      case S2C.START: onStart(m); break;
      case S2C.TURN: onTurnMsg(m); break;
      case S2C.DESYNC: ev.emit('desync', { turn: m.turn, hashes: m.hashes }); break;
      case S2C.PONG: onPong(m); break;
      case S2C.ERROR:
        if (m.rid !== undefined && requests.has(m.rid)) settle(m, false);
        else if (status === 'reconnecting' && m.code === 'expired') { giveUp('expired'); return; }
        ev.emit('error', { code: m.code, message: m.message || errorText(m.code) });
        break;
    }
  }

  function resetMatchState() {
    delivered = -1; reorder.clear(); localTurn = -1; sentThrough = -1; queue = []; pending.clear();
    stallTicks = 0; lastStallTick = -1; stalledSince = -1; lastResend = -1e9;
  }

  function onStart(m) {
    resetMatchState();
    tpt = m.ticksPerTurn;
    match = { seed: m.seed, setup: m.setup, turnMs: m.turnMs, ticksPerTurn: m.ticksPerTurn, graceMs: m.graceMs, players: m.players || [] };
    adaptDelay(true);
    ev.emit('start', { ...match, you: playerId });
  }

  function onResumed(m) {
    code = m.code; playerId = m.playerId; token = m.token || token;
    const wasReconnecting = status === 'reconnecting';
    status = 'open';
    clearTimeout(reconnectTimer);
    settle(m, true);
    if (m.start && !match) {
      // Fresh page (resume from a stored session): start from scratch and replay all turns.
      onStart(m.start);
    }
    if (match) {
      if (sentThrough < m.lastSub) sentThrough = m.lastSub; // server already holds these
      // Resend submissions the server may not have seen; it ignores duplicates by turn.
      for (const t of [...pending.keys()].sort((a, b) => a - b)) {
        const p = pending.get(t);
        rawSend({ type: C2S.CMDS, turn: t, cmds: p.cmds });
      }
    }
    ev.emit(wasReconnecting ? 'reconnected' : 'resumed', { code, playerId, nextTurn: m.nextTurn });
  }

  function onTurnMsg(m) {
    if (!match || m.turn <= delivered) return;
    if (m.turn !== delivered + 1) {
      if (reorder.size < 4096) reorder.set(m.turn, m);
      const t = now();
      if (t - lastReplayAsk > 1000) { lastReplayAsk = t; rawSend({ type: C2S.REPLAY, from: delivered + 1 }); }
      return;
    }
    deliver(m);
    while (reorder.has(delivered + 1)) { const n = reorder.get(delivered + 1); reorder.delete(delivered + 1); deliver(n); }
  }

  function deliver(m) {
    delivered = m.turn;
    const mine = pending.get(m.turn);
    if (mine) {
      const d = now() - mine.at;
      lat.n++; lat.sum += d; if (d > lat.max) lat.max = d; if (d < lat.min) lat.min = d;
    }
    for (const t of pending.keys()) if (t <= m.turn) pending.delete(t);
    const turn = { turn: m.turn, cmds: m.cmds };
    for (const cb of [...turnCbs]) { try { cb(turn); } catch (err) { console.error('lockstep: onTurn handler failed', err); } }
  }

  function onPong(m) {
    const r = now() - m.t;
    if (!(r >= 0 && r < 60000)) return;
    if (!rttSamples) { srtt = r; rttvar = r / 2; }
    else { rttvar = 0.75 * rttvar + 0.25 * Math.abs(srtt - r); srtt = 0.875 * srtt + 0.125 * r; }
    rttSamples++;
    roomMaxRtt = typeof m.maxRtt === 'number' && m.maxRtt >= 0 ? m.maxRtt : 0;
    adaptDelay(false);
  }

  /** Delay must cover a round trip to the farthest peer plus jitter, otherwise the sim stalls. */
  function adaptDelay(reset) {
    if (!adaptiveDelay) return;
    const turnMs = match?.turnMs || 100;
    const base = Math.max(srtt, roomMaxRtt);
    const target = rttSamples ? clampDelay(Math.ceil((base + 2 * rttvar + 10) / turnMs)) : delay;
    const old = delay;
    if (reset) { delay = Math.max(clampDelay(inputDelayTurns), target); lowerStreak = 0; }
    else if (target > delay) { delay = target; lowerStreak = 0; }
    else if (target < delay) { if (++lowerStreak >= 5) { delay--; lowerStreak = 0; } }
    else lowerStreak = 0;
    if (delay !== old) ev.emit('delay', { delay, rtt: srtt, maxRtt: roomMaxRtt });
  }

  /* ---------------- submission ---------------- */

  /** Take as many queued commands as fit the per-turn budget. */
  function takeBatch() {
    if (!queue.length) return [];
    let bytes = 2, n = 0;
    while (n < queue.length && n < LIMITS.CMDS_PER_TURN) {
      const s = queue[n].size + 1;
      if (bytes + s > LIMITS.CMDS_BYTES) break;
      bytes += s; n++;
    }
    return queue.splice(0, n).map(q => q.cmd);
  }

  /** The sim entered turn `lt`: every turn up to lt-1+delay can now be submitted. */
  function progress(lt) {
    if (!match || lt <= localTurn) return;
    localTurn = lt;
    const target = lt - 1 + delay;
    while (sentThrough < target) {
      const T = sentThrough + 1;
      ev.emit('submit', { turn: T });          // last chance to queue coalesced input for this turn
      const cmds = takeBatch();
      sentThrough = T;
      pending.set(T, { cmds, at: now() });
      rawSend({ type: C2S.CMDS, turn: T, cmds }); // if offline, it is resent after resume
    }
  }

  /** Liveness: if a turn is overdue, resend unconfirmed submissions (the relay drops duplicates). */
  function watchdog() {
    const t = now();
    if (stalledSince < 0) { stalledSince = t; return; }
    const limit = Math.max(400, 4 * (match?.turnMs || 100));
    if (t - stalledSince < limit || t - lastResend < limit || status !== 'open') return;
    lastResend = t; resends++;
    for (const turn of [...pending.keys()].sort((a, b) => a - b)) rawSend({ type: C2S.CMDS, turn, cmds: pending.get(turn).cmds });
    if (reorder.size) rawSend({ type: C2S.REPLAY, from: delivered + 1 });
  }

  /* ---------------- public API ---------------- */

  const api = {
    on: ev.on, off: ev.off,

    connect,

    async createRoom() {
      if (code) throw new LockstepError('already_in_room');
      await routeTo(null);
      const m = await request({ type: C2S.CREATE, name });
      return { code: m.code, playerId: m.playerId };
    },

    async joinRoom(roomCode) {
      if (code) throw new LockstepError('already_in_room');
      const c = cleanCode(roomCode);
      if (!c) throw new LockstepError('not_found');
      await routeTo(c);
      const m = await request({ type: C2S.JOIN, code: c, name });
      return { code: m.code, playerId: m.playerId };
    },

    /** Resume a stored session (e.g. after a page reload within the reconnect window). */
    async resume(session) {
      if (!session || !session.code || !session.token) throw new LockstepError('expired');
      await routeTo(cleanCode(session.code));
      code = session.code; token = session.token;
      try {
        const m = await request({ type: C2S.RESUME, code: session.code, token: session.token, lastTurn: match ? delivered : -1 });
        return { code: m.code, playerId: m.playerId };
      } catch (err) { code = null; token = null; throw err; }
    },

    leave() {
      if (!code) return;
      rawSend({ type: C2S.LEAVE });
      code = null; token = null; playerId = 0; lobby = null; match = null; resetMatchState();
    },

    setTeam(t) { rawSend({ type: C2S.TEAM, team: t ? 1 : 0 }); },
    setReady(b) { rawSend({ type: C2S.READY, ready: !!b }); },

    /** Host only: start the match. `setup` is opaque JSON broadcast to every client. */
    start({ seed, setup = null } = {}) {
      if (seed === undefined) seed = (Math.random() * 0x100000000) >>> 0;
      rawSend({ type: C2S.START, seed: seed >>> 0, setup });
    },

    /** Queue a command (plain JSON object). It runs on every client at turn current+delay. */
    queueCommand(cmd) {
      const size = commandSize(cmd);
      if (size < 0 || size > LIMITS.CMD_BYTES) { ev.emit('error', { code: 'cmds_too_large', message: errorText('cmds_too_large') }); return false; }
      if (!match) return false;
      if (queue.length >= 1024) return false;
      queue.push({ cmd, size });
      return true;
    },

    /** Subscribe to turns (strictly ordered, never skipping). Returns an unsubscribe function. */
    onTurn(cb) { turnCbs.add(cb); return () => turnCbs.delete(cb); },

    /**
     * Call before executing sim tick `simTick` (0-based). Returns false if the turn covering that
     * tick has not arrived yet; the sim must stall. Also drives command submission.
     */
    canAdvance(simTick) {
      if (!match) return false;
      const lt = Math.floor(simTick / tpt);
      progress(lt);
      const ok = lt <= delivered;
      if (ok) stalledSince = -1;
      else {
        if (simTick !== lastStallTick) { lastStallTick = simTick; stallTicks++; }
        watchdog();
      }
      return ok;
    },

    /** Turns that are delivered beyond the one containing `simTick` (use > 2 to run catch-up ticks). */
    bufferedTurns(simTick) { return delivered - Math.floor(simTick / tpt); },

    /** Report the state hash after executing the last tick of `turn`. */
    reportHash(turn, hash) { rawSend({ type: C2S.HASH, turn, hash: hash >>> 0 }); },

    /** Debug/test helper: drop the socket as if the network failed (the client auto-reconnects). */
    simulateDrop() { try { ws?.close(4000, 'drop'); } catch { } },

    close() {
      intentional = true;
      clearTimeout(reconnectTimer); stopPing();
      if (code) rawSend({ type: C2S.LEAVE });
      code = null; token = null;
      const s = ws; ws = null;
      if (s) { try { s.close(1000, 'bye'); } catch { } }
      if (status !== 'closed') { status = 'closed'; ev.emit('close', { reason: 'left' }); }
    },

    get status() { return status; },
    get code() { return code; },
    get playerId() { return playerId; },
    get isHost() { return !!lobby && lobby.hostId === playerId; },
    get lobby() { return lobby; },
    get match() { return match; },
    /** Store this (e.g. sessionStorage) to call resume() after a page reload. */
    get session() { return code && token ? { code, token, playerId } : null; },
    get rtt() { return srtt; },
    get maxRtt() { return roomMaxRtt; },
    get inputDelay() { return delay; },
    set inputDelay(d) { delay = clampDelay(d); },
    get ticksPerTurn() { return tpt; },
    get turn() { return delivered; },
    get localTurn() { return localTurn; },
    get stats() {
      return {
        rtt: srtt, rttVar: rttvar, maxRtt: roomMaxRtt, inputDelay: delay, delivered, localTurn, sentThrough,
        stallTicks, resends, turnLatencyAvg: lat.n ? lat.sum / lat.n : 0, turnLatencyMax: lat.max,
        turnLatencyMin: lat.n ? lat.min : 0, turnLatencySamples: lat.n,
      };
    },
  };
  return api;
}

/**
 * Worker-side turn buffer: main thread forwards each `{ turn, cmds }` via postMessage, the sim
 * worker checks `has(tick)` before every tick and takes the commands at turn boundaries.
 */
export function createTurnBuffer(ticksPerTurn = 3) {
  const turns = new Map();
  let next = 0;                              // next turn expected from push()
  return {
    push(t) {
      if (!t || t.turn !== next) return false; // the client delivers strictly in order
      turns.set(t.turn, t.cmds || []); next++;
      return true;
    },
    has(tick) { return Math.floor(tick / ticksPerTurn) < next; },
    /** Commands to apply before executing `tick` (only at the first tick of a turn), else null. */
    take(tick) {
      if (tick % ticksPerTurn !== 0) return null;
      const T = tick / ticksPerTurn, c = turns.get(T);
      turns.delete(T);
      return c || null;
    },
    ahead(tick) { return next - 1 - Math.floor(tick / ticksPerTurn); },
    get next() { return next; },
    reset() { turns.clear(); next = 0; },
  };
}
